/**
 * Builds a self-contained HTML string with embedded CSS + vanilla JS player.
 * No React, no Zustand, no sql.js — runs in any modern browser.
 */

import type { ExportData } from './htmlExporter.ts';
import { APPROVAL_COLORS, LAYOUT, AUDIO_LANE_NAMES, AUDIO_SUBLANE_HEIGHT } from './playerConstants.ts';

export function buildPlayerHtml(data: ExportData): string {
  const jsonPayload = JSON.stringify(data);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${escapeHtml(data.title)} — Timeline Review</title>
${PLAYER_STYLE}
</head>
<body>
<script>
// ── Data payload ──────────────────────────────────────────────────────
var __DATA__ = ${jsonPayload};
</script>
<script>
(function() {
'use strict';

${PLAYER_STATE}

${PLAYER_HELPERS}

${PLAYER_DOM}

${PLAYER_PLAYBACK}

${PLAYER_AUDIO}

${PLAYER_TRANSITIONS}

${PLAYER_VIDEO}

${PLAYER_COMMENTS}

${PLAYER_OVERLAYS}

${PLAYER_INIT}

})();
</script>
</body>
</html>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ═══════════════════════════════════════════════════════════════════════════
// CSS
// ═══════════════════════════════════════════════════════════════════════════

const PLAYER_STYLE = `<style>
:root {
  --bg-primary: #111111;
  --bg-secondary: #1a1a1a;
  --bg-tertiary: #222222;
  --bg-elevated: #2a2a2a;
  --bg-hover: #333333;
  --text-primary: #e8e8e8;
  --text-secondary: #a0a0a0;
  --text-muted: #666666;
  --border-color: #333333;
  --border-subtle: #222222;
  --accent: #c43c2d;
  --accent-dim: #a83225;
  --playhead: #e04040;
  --playhead-glow: rgba(224, 64, 64, 0.4);
  --font-mono: 'SF Mono', 'Menlo', 'Monaco', 'Consolas', monospace;
  --font-sans: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --transport-height: 48px;
  --ruler-height: 24px;
  --shot-lane-height: 80px;
  --audio-lane-height: 80px;
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

html, body {
  height: 100%;
  width: 100%;
  overflow: hidden;
  font-family: var(--font-sans);
  background: var(--bg-primary);
  color: var(--text-primary);
  -webkit-font-smoothing: antialiased;
  user-select: none;
}

button {
  font-family: inherit;
  cursor: pointer;
  border: none;
  background: none;
  color: inherit;
}

button:disabled { cursor: default; opacity: 0.4; }

/* ── Layout ────────────────────────────────────────────────────────── */

#app {
  display: flex;
  flex-direction: column;
  height: 100%;
}

#header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border-subtle);
  flex: none;
  min-height: 40px;
}

#header .title {
  font-size: 14px;
  font-weight: 600;
}

#header .meta {
  font-size: 11px;
  color: var(--text-muted);
}

#header .badge {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  background: var(--bg-hover);
  color: var(--text-secondary);
}

/* ── Middle area: viewer + comments ────────────────────────────────── */

#middle {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

#viewer-area {
  flex: 1;
  position: relative;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

#viewer-area img,
#viewer-area video {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

#viewer-area .transition-stack {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

#viewer-area .transition-layer {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

#viewer-area .transition-layer img,
#viewer-area .transition-layer video {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.placeholder-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
  width: 100%;
  height: 100%;
}

.placeholder-card .shot-num {
  font-size: 48px;
  font-weight: 700;
  opacity: 0.3;
}

.placeholder-card .heading {
  font-size: 13px;
  font-weight: 600;
  opacity: 0.7;
}

.placeholder-card .lens {
  font-size: 11px;
  color: var(--text-secondary);
}

.placeholder-card .meta-badge {
  font-size: 10px;
  color: var(--text-muted);
}

.placeholder-card .notes {
  font-size: 11px;
  color: var(--text-secondary);
  opacity: 0.7;
  max-width: 300px;
}

/* Content overlay — doodles + movement arrows.
   Sized in JS to the LETTERBOXED shot rect, not inset:0 on #viewer-area: at any project aspect
   other than the window's, the image is centred with matte on two sides and an inset layer would
   stretch the strokes and park the edge-anchored arrows on the matte. Sits under #shot-label /
   #review-overlay / #fullscreen-btn by DOM order. */
#content-overlay {
  position: absolute;
  pointer-events: none;
  overflow: hidden;
}

#content-overlay .doodle-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

/* The arrow sprites are WHITE glyphs on alpha — Unity tints them at the Image component, so an
   <img> here would draw white-on-white and show nothing at all. Masked and filled with the
   app-standard markup red (#D73800), a literal that has to match ShotMovementOverlay.module.css,
   the Unity canvas, and Unity's own image/PDF export. */
.movement-arrow {
  position: absolute;
  background-color: #D73800;
  -webkit-mask-image: var(--arw);
  mask-image: var(--arw);
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

/* Shot label overlay */
#shot-label {
  position: absolute;
  bottom: 8px;
  left: 8px;
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: var(--text-secondary);
  text-shadow: 0 1px 3px rgba(0,0,0,0.8);
  pointer-events: none;
}

/* Review overlay */
#review-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.approval-pill {
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  color: #fff;
  text-transform: capitalize;
  cursor: pointer;
}

.comment-count-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s;
}

.comment-count-btn:hover {
  background: rgba(255,255,255,0.25);
}

/* ── Comments sidebar ──────────────────────────────────────────────── */

#comments-panel {
  width: 0;
  overflow: hidden;
  background: var(--bg-secondary);
  border-left: 1px solid var(--border-subtle);
  transition: width 0.2s ease;
  flex: none;
}

#comments-panel.open {
  width: 280px;
}

#comments-panel .panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-subtle);
  font-size: 13px;
  font-weight: 600;
}

#comments-panel .close-btn {
  font-size: 16px;
  color: var(--text-muted);
  cursor: pointer;
}

#comments-panel .close-btn:hover { color: var(--text-primary); }

#comments-panel .comments-list {
  padding: 8px 12px;
  overflow-y: auto;
  max-height: calc(100% - 40px);
}

.comment-item {
  margin-bottom: 12px;
  padding: 8px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  font-size: 12px;
}

.comment-item.reply {
  margin-left: 16px;
  border-left: 2px solid var(--border-color);
}

.comment-item.resolved {
  opacity: 0.5;
}

.comment-author {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.comment-time {
  font-size: 10px;
  color: var(--text-muted);
  margin-bottom: 4px;
}

.comment-text {
  color: var(--text-secondary);
  line-height: 1.4;
}

.comment-resolved-badge {
  font-size: 9px;
  color: #4caf50;
  margin-top: 4px;
}

.comment-doodle-badge {
  display: inline-flex;
  vertical-align: middle;
  margin-left: 6px;
  color: var(--accent);
}

/* ── Timeline area ─────────────────────────────────────────────────── */

#timeline-area {
  flex: none;
  border-top: 1px solid var(--border-subtle);
  background: var(--bg-secondary);
}

/* Ruler */
#ruler {
  position: relative;
  height: var(--ruler-height);
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-subtle);
  overflow: hidden;
  cursor: pointer;
}

#ruler .tick {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--border-color);
}

#ruler .tick-label {
  position: absolute;
  top: 2px;
  font-size: 9px;
  font-family: var(--font-mono);
  color: var(--text-muted);
  white-space: nowrap;
}

/* Shot lane */
#shot-lane {
  position: relative;
  height: var(--shot-lane-height);
  overflow: hidden;
  cursor: pointer;
}

#shot-lane .shot-bar {
  position: absolute;
  top: 4px;
  bottom: 4px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s;
  border: 1px solid rgba(255,255,255,0.08);
}

#shot-lane .shot-bar:hover { border-color: rgba(255,255,255,0.2); }

#shot-lane .shot-bar.active {
  border-color: var(--playhead);
  box-shadow: 0 0 6px var(--playhead-glow);
}

.shot-bar-fill {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  position: relative;
}

.shot-bar-fill img {
  height: 100%;
  object-fit: contain;
  object-position: left center;
}

.shot-bar-fill .placeholder-num {
  font-size: 16px;
  font-weight: 700;
  opacity: 0.4;
  font-family: var(--font-mono);
}

/* Info pill — compact overlay at bottom-right */
.shot-bar-info {
  position: absolute;
  bottom: 2px;
  right: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.55);
  font-size: 9px;
  color: var(--text-secondary);
  white-space: nowrap;
  pointer-events: none;
  z-index: 2;
}

.shot-bar-info .shot-num {
  font-weight: 600;
  color: var(--text-primary);
}

/* Transition diamond */
.transition-diamond {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%) rotate(45deg);
  width: 10px;
  height: 10px;
  background: var(--accent);
  border: 1px solid rgba(255,255,255,0.2);
  z-index: 2;
  pointer-events: none;
}

/* Playhead */
#playhead {
  position: absolute;
  top: 0;
  width: 2px;
  background: var(--playhead);
  box-shadow: 0 0 6px var(--playhead-glow);
  z-index: 10;
  pointer-events: none;
}

/* Audio lane */
#audio-lane {
  position: relative;
  height: var(--audio-lane-height);
  background: var(--bg-tertiary);
  border-top: 1px solid var(--border-subtle);
  overflow: hidden;
  display: none;
}

#audio-lane.visible { display: block; }

.audio-clip-bar {
  position: absolute;
  height: 20px;
  border-radius: 3px;
  overflow: hidden;
  opacity: 0.8;
}

.audio-clip-bar canvas {
  width: 100%;
  height: 100%;
}

.audio-clip-label {
  position: absolute;
  top: 0;
  left: 4px;
  right: 0;
  font-size: 9px;
  line-height: 20px;
  color: rgba(255,255,255,0.9);
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
  pointer-events: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  z-index: 1;
}

.audio-lane-divider {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: rgba(255,255,255,0.06);
  pointer-events: none;
}

.audio-lane-label {
  position: absolute;
  left: 4px;
  font-size: 8px;
  font-weight: 600;
  line-height: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255,255,255,0.25);
  pointer-events: none;
  z-index: 2;
}

/* ── Transport ─────────────────────────────────────────────────────── */

#transport {
  display: flex;
  align-items: center;
  gap: 12px;
  height: var(--transport-height);
  padding: 0 16px;
  background: var(--bg-elevated);
  border-top: 1px solid var(--border-subtle);
  flex: none;
}

#transport .btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  font-size: 16px;
  transition: background 0.15s;
}

#transport .btn:hover { background: var(--bg-hover); }

#transport .play-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--accent);
  font-size: 18px;
}

#transport .play-btn:hover { background: var(--accent-dim); }

.timecode {
  font-family: var(--font-mono);
  font-size: 14px;
  color: var(--text-primary);
  min-width: 80px;
  text-align: center;
}

.separator {
  color: var(--text-muted);
  font-size: 12px;
}

.total-time {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
}

.spacer { flex: 1; }

.project-info {
  font-size: 12px;
  color: var(--text-muted);
}

/* ── Scrollbar styling ─────────────────────────────────────────────── */

::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--border-color); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: var(--text-muted); }

/* ── Timeline scroll container ─────────────────────────────────────── */

#timeline-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
}

#timeline-inner {
  position: relative;
  min-width: 100%;
}

/* ── Fullscreen button ────────────────────────────────────────────── */

#fullscreen-btn {
  position: absolute;
  bottom: 6px;
  right: 6px;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: var(--text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  opacity: 0;
  transition: opacity 0.2s, background 0.15s;
}

#viewer-area:hover #fullscreen-btn { opacity: 0.85; }
#fullscreen-btn:hover { opacity: 1 !important; background: rgba(0, 0, 0, 0.8); }

/* ── Comment dot on shot bars ─────────────────────────────────────── */

.shot-comment-dot {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  pointer-events: none;
  z-index: 3;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.4);
}

/* ── Video badge on shot bars ─────────────────────────────────────── */

.shot-video-badge {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

/* ── Zoom slider in transport ─────────────────────────────────────── */

.zoom-controls {
  display: flex;
  align-items: center;
}

.zoom-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 72px;
  height: 4px;
  border-radius: 2px;
  background: var(--border-color);
  outline: none;
  cursor: pointer;
}

.zoom-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--text-secondary);
  border: none;
  cursor: pointer;
  transition: background 0.15s;
}

.zoom-slider::-webkit-slider-thumb:hover { background: var(--text-primary); }

.zoom-slider::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--text-secondary);
  border: none;
  cursor: pointer;
}
</style>`;

// ═══════════════════════════════════════════════════════════════════════════
// JS Sections
// ═══════════════════════════════════════════════════════════════════════════

const PLAYER_STATE = `
// ── State ──────────────────────────────────────────────────────────────
var data = __DATA__;
var currentTime = 0;
var isPlaying = false;
var selectedShotId = null;
var commentPanelOpen = false;
var pixelsPerSecond = 80;
var scrollLeft = 0;
var audioCtx = null;
var activeSources = [];
var decodedBuffers = {}; // clipId -> AudioBuffer
var videoElements = {};  // shotId -> HTMLVideoElement (for data URL videos)
var rafId = 0;
var lastFrameTime = 0;
`;

const PLAYER_HELPERS = `
// ── Helpers ────────────────────────────────────────────────────────────

function allShots() {
  var shots = [];
  for (var i = 0; i < data.scenes.length; i++) {
    for (var j = 0; j < data.scenes[i].shots.length; j++) {
      shots.push(data.scenes[i].shots[j]);
    }
  }
  return shots;
}

function getShotAtTime(t) {
  var shots = allShots();
  var cum = 0;
  for (var i = 0; i < shots.length; i++) {
    if (t >= cum && t < cum + shots[i].duration) return shots[i];
    cum += shots[i].duration;
  }
  return shots.length > 0 ? shots[shots.length - 1] : null;
}

function getShotStartTime(id) {
  var shots = allShots();
  var cum = 0;
  for (var i = 0; i < shots.length; i++) {
    if (shots[i].id === id) return cum;
    cum += shots[i].duration;
  }
  return 0;
}

function getSceneForShot(shotId) {
  for (var i = 0; i < data.scenes.length; i++) {
    var scene = data.scenes[i];
    for (var j = 0; j < scene.shots.length; j++) {
      if (scene.shots[j].id === shotId) return scene;
    }
  }
  return null;
}

function getTransitionAtTime(t) {
  var shots = allShots();
  var cum = 0;
  for (var i = 0; i < shots.length - 1; i++) {
    cum += shots[i].duration;
    var trans = null;
    for (var k = 0; k < data.transitions.length; k++) {
      var tr = data.transitions[k];
      if (tr.fromShotId === shots[i].id && tr.toShotId === shots[i + 1].id) {
        trans = tr;
        break;
      }
    }
    if (!trans || trans.type === 'cut') continue;
    var halfDur = trans.duration / 2;
    var ws = cum - halfDur;
    var we = cum + halfDur;
    if (t >= ws && t < we) {
      var progress = (t - ws) / trans.duration;
      return {
        transition: trans,
        fromShot: shots[i],
        toShot: shots[i + 1],
        progress: Math.max(0, Math.min(1, progress))
      };
    }
  }
  return null;
}

function getApprovalForShot(shotId) {
  var best = null;
  for (var i = 0; i < data.approvals.length; i++) {
    var a = data.approvals[i];
    if (a.shotId === shotId) {
      if (!best || a.revision > best.revision) best = a;
    }
  }
  return best;
}

function getCommentsForShot(shotId) {
  var result = [];
  for (var i = 0; i < data.comments.length; i++) {
    if (data.comments[i].shotId === shotId) result.push(data.comments[i]);
  }
  return result;
}

function getCommentCount(shotId) {
  var count = 0;
  for (var i = 0; i < data.comments.length; i++) {
    var c = data.comments[i];
    if (c.shotId === shotId && !c.parentCommentId) count++;
  }
  return count;
}

function formatTimecode(seconds) {
  var mins = Math.floor(seconds / 60);
  var secs = seconds % 60;
  return (mins < 10 ? '0' : '') + mins + ':' + (secs < 10 ? '0' : '') + secs.toFixed(1);
}

// Port of utils/aspectRatio.ts. 0 means unparseable — callers fall back to 16:9, the loader default.
function parseAspectRatio(ar) {
  var parts = String(ar || '').split(':');
  if (parts.length === 2) {
    var w = parseFloat(parts[0]);
    var h = parseFloat(parts[1]);
    if (w > 0 && h > 0) return w / h;
  }
  return 0;
}

// Port of computeContentRect (ViewerContent.tsx) — the object-fit:contain box inside the viewer.
function computeContentRect(containerW, containerH, naturalW, naturalH) {
  if (naturalW === 0 || naturalH === 0) {
    return { x: 0, y: 0, width: containerW, height: containerH };
  }
  var scale = Math.min(containerW / naturalW, containerH / naturalH);
  var w = naturalW * scale;
  var h = naturalH * scale;
  return { x: (containerW - w) / 2, y: (containerH - h) / 2, width: w, height: h };
}

var APPROVAL_COLORS = ${JSON.stringify(APPROVAL_COLORS)};

function toggleFullscreen() {
  var viewerArea = document.getElementById('viewer-area');
  if (!viewerArea) return;
  if (document.fullscreenElement || document.webkitFullscreenElement) {
    (document.exitFullscreen || document.webkitExitFullscreen).call(document);
  } else {
    (viewerArea.requestFullscreen || viewerArea.webkitRequestFullscreen).call(viewerArea);
  }
}
`;

const PLAYER_DOM = `
// ── DOM construction ──────────────────────────────────────────────────

function buildUI() {
  var app = document.createElement('div');
  app.id = 'app';

  // Header
  var header = document.createElement('div');
  header.id = 'header';
  var titleEl = document.createElement('span');
  titleEl.className = 'title';
  titleEl.textContent = data.title;
  header.appendChild(titleEl);

  var shots = allShots();
  var metaEl = document.createElement('span');
  metaEl.className = 'meta';
  metaEl.textContent = data.scenes.length + ' scene' + (data.scenes.length !== 1 ? 's' : '') +
    ', ' + shots.length + ' shot' + (shots.length !== 1 ? 's' : '') +
    ' \\u2022 ' + formatTimecode(data.totalDuration);
  header.appendChild(metaEl);

  var arBadge = document.createElement('span');
  arBadge.className = 'badge';
  arBadge.textContent = data.aspectRatio;
  header.appendChild(arBadge);

  app.appendChild(header);

  // Middle: viewer + comments
  var middle = document.createElement('div');
  middle.id = 'middle';

  var viewerArea = document.createElement('div');
  viewerArea.id = 'viewer-area';
  middle.appendChild(viewerArea);

  // Before the label/review chrome on purpose: DOM order is the only z-order here, and the
  // annotations belong over the media but under the readable overlays.
  var contentOverlay = document.createElement('div');
  contentOverlay.id = 'content-overlay';
  viewerArea.appendChild(contentOverlay);

  var shotLabel = document.createElement('div');
  shotLabel.id = 'shot-label';
  viewerArea.appendChild(shotLabel);

  var reviewOverlay = document.createElement('div');
  reviewOverlay.id = 'review-overlay';
  viewerArea.appendChild(reviewOverlay);

  // Fullscreen button
  var fsBtn = document.createElement('button');
  fsBtn.id = 'fullscreen-btn';
  fsBtn.title = 'Toggle fullscreen (F)';
  fsBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15,3 21,3 21,9"/><polyline points="9,21 3,21 3,15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>';
  fsBtn.onclick = function() { toggleFullscreen(); };
  viewerArea.appendChild(fsBtn);

  // Comments panel
  var commentsPanel = document.createElement('div');
  commentsPanel.id = 'comments-panel';

  var panelHeader = document.createElement('div');
  panelHeader.className = 'panel-header';
  var panelTitle = document.createElement('span');
  panelTitle.textContent = 'Comments';
  panelHeader.appendChild(panelTitle);
  var closeBtn = document.createElement('span');
  closeBtn.className = 'close-btn';
  closeBtn.textContent = '\\u2715';
  closeBtn.onclick = function() { toggleComments(); };
  panelHeader.appendChild(closeBtn);
  commentsPanel.appendChild(panelHeader);

  var commentsList = document.createElement('div');
  commentsList.className = 'comments-list';
  commentsPanel.appendChild(commentsList);
  middle.appendChild(commentsPanel);

  app.appendChild(middle);

  // Timeline area
  var timelineArea = document.createElement('div');
  timelineArea.id = 'timeline-area';

  var timelineScroll = document.createElement('div');
  timelineScroll.id = 'timeline-scroll';

  var timelineInner = document.createElement('div');
  timelineInner.id = 'timeline-inner';

  // Ruler
  var ruler = document.createElement('div');
  ruler.id = 'ruler';
  timelineInner.appendChild(ruler);

  // Shot lane
  var shotLane = document.createElement('div');
  shotLane.id = 'shot-lane';
  timelineInner.appendChild(shotLane);

  // Playhead (spans ruler + shot lane + audio lane)
  var playhead = document.createElement('div');
  playhead.id = 'playhead';
  timelineInner.appendChild(playhead);

  // Audio lane
  var audioLane = document.createElement('div');
  audioLane.id = 'audio-lane';
  if (data.audioClips.length > 0) audioLane.className = 'visible';
  timelineInner.appendChild(audioLane);

  timelineScroll.appendChild(timelineInner);
  timelineArea.appendChild(timelineScroll);
  app.appendChild(timelineArea);

  // Transport
  var transport = document.createElement('div');
  transport.id = 'transport';
  transport.innerHTML =
    '<button class="btn" id="skip-back" title="Back 1s (Left)"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="5" width="2.5" height="14" rx="0.5"/><path d="M20 5.5v13a1 1 0 01-1.54.84l-6-3.9V18.5a1 1 0 01-1.54.84l-6-3.9a1 1 0 010-1.68l6-3.9a1 1 0 011.54.84v3.06l6-3.9A1 1 0 0120 5.5z"/></svg></button>' +
    '<button class="btn play-btn" id="play-btn" title="Play/Pause (Space)"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6.5 4.12v15.76a1 1 0 001.52.86l12.36-7.88a1 1 0 000-1.72L8.02 3.26a1 1 0 00-1.52.86z"/></svg></button>' +
    '<button class="btn" id="skip-fwd" title="Forward 1s (Right)"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="17.5" y="5" width="2.5" height="14" rx="0.5"/><path d="M4 5.5v13a1 1 0 001.54.84l6-3.9v3.06a1 1 0 001.54.84l6-3.9a1 1 0 000-1.68l-6-3.9a1 1 0 00-1.54.84v3.06l-6-3.9A1 1 0 004 5.5z"/></svg></button>' +
    '<span class="timecode" id="timecode-display">00:00.0</span>' +
    '<span class="separator">/</span>' +
    '<span class="total-time">' + formatTimecode(data.totalDuration) + '</span>' +
    '<div class="zoom-controls"><input type="range" class="zoom-slider" id="zoom-slider" min="20" max="300" step="10" value="' + pixelsPerSecond + '" title="Zoom (+ / -)"></div>' +
    '<div class="spacer"></div>' +
    '<span class="project-info">' + data.scenes.length + ' scene' + (data.scenes.length !== 1 ? 's' : '') +
    ', ' + shots.length + ' shot' + (shots.length !== 1 ? 's' : '') + '</span>';
  app.appendChild(transport);

  document.body.appendChild(app);

  // Auto-fit zoom
  computeZoom();
}

function computeZoom() {
  var scrollEl = document.getElementById('timeline-scroll');
  if (!scrollEl) return;
  var vpWidth = scrollEl.clientWidth - 40;
  var fit = vpWidth / data.totalDuration;
  pixelsPerSecond = Math.max(40, fit);
  syncZoomSlider();
  renderTimeline();
}

function syncZoomSlider() {
  var slider = document.getElementById('zoom-slider');
  if (slider) slider.value = String(Math.round(pixelsPerSecond));
}
`;

const PLAYER_PLAYBACK = `
// ── Playback ──────────────────────────────────────────────────────────

function play() {
  if (currentTime >= data.totalDuration) currentTime = 0;
  isPlaying = true;
  lastFrameTime = performance.now();
  scheduleAudio(currentTime);
  syncVideoPlay();
  updatePlayBtn();
  tick();
}

function pause() {
  isPlaying = false;
  if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
  stopAllAudio();
  syncVideoPause();
  updatePlayBtn();
}

function togglePlay() {
  if (isPlaying) pause(); else play();
}

function seekTo(t) {
  var wasPlaying = isPlaying;
  if (isPlaying) {
    cancelAnimationFrame(rafId);
    rafId = 0;
    stopAllAudio();
  }
  currentTime = Math.max(0, Math.min(t, data.totalDuration));
  updateViewer();
  updateTimeline();
  updateTimecodeDisplay();
  syncVideoSeek();
  if (wasPlaying) {
    lastFrameTime = performance.now();
    scheduleAudio(currentTime);
    syncVideoPlay();
    tick();
  }
}

function tick() {
  if (!isPlaying) return;
  var now = performance.now();
  var dt = (now - lastFrameTime) / 1000;
  lastFrameTime = now;
  currentTime += dt;

  if (currentTime >= data.totalDuration) {
    currentTime = data.totalDuration;
    updateViewer();
    updateTimeline();
    updateTimecodeDisplay();
    pause();
    return;
  }

  updateViewer();
  updateTimeline();
  updateTimecodeDisplay();
  autoScroll();
  rafId = requestAnimationFrame(tick);
}

function updatePlayBtn() {
  var btn = document.getElementById('play-btn');
  if (btn) btn.innerHTML = isPlaying
    ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="4" width="5" height="16" rx="1"/><rect x="14" y="4" width="5" height="16" rx="1"/></svg>'
    : '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6.5 4.12v15.76a1 1 0 001.52.86l12.36-7.88a1 1 0 000-1.72L8.02 3.26a1 1 0 00-1.52.86z"/></svg>';
}

function updateTimecodeDisplay() {
  var el = document.getElementById('timecode-display');
  if (el) el.textContent = formatTimecode(currentTime);
}

function autoScroll() {
  var scrollEl = document.getElementById('timeline-scroll');
  if (!scrollEl) return;
  var px = currentTime * pixelsPerSecond;
  var viewLeft = scrollEl.scrollLeft;
  var viewRight = viewLeft + scrollEl.clientWidth;
  if (px > viewRight - 40 || px < viewLeft) {
    scrollEl.scrollLeft = px - 60;
  }
}
`;

const PLAYER_AUDIO = `
// ── Audio ─────────────────────────────────────────────────────────────

function initAudioContext() {
  if (audioCtx) return audioCtx;
  audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return audioCtx;
}

function preDecodeAudio() {
  // Decode all audio clips from base64 or URL on page load
  for (var i = 0; i < data.audioClips.length; i++) {
    var clip = data.audioClips[i];
    if (clip.mediaUrl) {
      // URL mode: fetch from CDN
      (function(clipId, url) {
        fetch(url).then(function(r) { return r.arrayBuffer(); })
          .then(function(buf) { return initAudioContext().decodeAudioData(buf); })
          .then(function(audioBuf) { decodedBuffers[clipId] = audioBuf; onClipDecoded(clipId); })
          .catch(function(e) { console.warn('Audio decode failed', clipId, e); });
      })(clip.id, clip.mediaUrl);
      continue;
    }
    if (!clip.rawBase64) continue;
    (function(clipId, dataUrl) {
      // Strip data URL prefix to get raw base64
      var b64 = dataUrl.indexOf(',') > -1 ? dataUrl.split(',')[1] : dataUrl;
      var binary = atob(b64);
      var bytes = new Uint8Array(binary.length);
      for (var j = 0; j < binary.length; j++) bytes[j] = binary.charCodeAt(j);
      var ctx = initAudioContext();
      ctx.decodeAudioData(bytes.buffer.slice(0)).then(function(buf) {
        decodedBuffers[clipId] = buf;
        onClipDecoded(clipId);
      }).catch(function(e) {
        console.warn('Failed to decode audio clip ' + clipId, e);
      });
    })(clip.id, clip.rawBase64);
  }
}

// Schedules a single decoded clip relative to the playhead, regardless of
// whether the clip is currently playing or starts in the future. Web Audio
// holds the schedule, so we don't need to keep re-running the loop as the
// timeline advances. Used by scheduleAudio() (full pass) and onClipDecoded()
// (one clip at a time, after its buffer arrives mid-playback).
function scheduleClipFromTime(clip, time) {
  if (!audioCtx) return;
  if (clip.isMuted) return;
  var buf = decodedBuffers[clip.id];
  if (!buf) return;
  var clipEnd = clip.startTime + clip.duration;
  if (time >= clipEnd) return; // already past

  // Map the clip into AudioContext time. For a clip that starts in the
  // future (relative to the playhead), source.start fires at ctxNow + delay
  // with offset 0. For a clip the playhead is already inside, we start it
  // immediately at the appropriate offset within the buffer.
  // bufferOffset is where this clip's content STARTS inside its decoded buffer — non-zero for a
  // left-trimmed clip or the right half of a split. Added to the offset exactly as the live app does
  // (useAudioPlayback: time - absStart + bufferOffset), which also means the fade math below sees the
  // same buffer-relative number the app's applyFades sees. Player and app must agree; a player that
  // drops the term reads from the buffer head and plays the material the user trimmed off.
  var ctxNow = audioCtx.currentTime;
  var bufferOffset = clip.bufferOffset || 0;
  var offsetIntoClip, whenInCtx;
  if (time < clip.startTime) {
    offsetIntoClip = bufferOffset;
    whenInCtx = ctxNow + (clip.startTime - time);
  } else {
    offsetIntoClip = time - clip.startTime + bufferOffset;
    whenInCtx = ctxNow;
  }

  var source = audioCtx.createBufferSource();
  source.buffer = buf;
  var gain = audioCtx.createGain();
  gain.gain.value = clip.volume;

  // Fade in
  if (clip.fadeInTime > 0 && offsetIntoClip < clip.fadeInTime) {
    var startGain = clip.volume * (offsetIntoClip / clip.fadeInTime);
    gain.gain.setValueAtTime(startGain, whenInCtx);
    gain.gain.linearRampToValueAtTime(clip.volume, whenInCtx + (clip.fadeInTime - offsetIntoClip));
  }

  // Fade out
  if (clip.fadeOutTime > 0) {
    var fadeOutStart = clip.duration - clip.fadeOutTime;
    if (offsetIntoClip < fadeOutStart) {
      gain.gain.setValueAtTime(clip.volume, whenInCtx + (fadeOutStart - offsetIntoClip));
      gain.gain.linearRampToValueAtTime(0, whenInCtx + (clip.duration - offsetIntoClip));
    } else {
      var partialGain = clip.volume * (1 - (offsetIntoClip - fadeOutStart) / clip.fadeOutTime);
      gain.gain.setValueAtTime(partialGain, whenInCtx);
      gain.gain.linearRampToValueAtTime(0, whenInCtx + (clip.duration - offsetIntoClip));
    }
  }

  source.connect(gain);
  gain.connect(audioCtx.destination);
  source.start(whenInCtx, offsetIntoClip);
  activeSources.push({ source: source, gain: gain, clipId: clip.id });
}

function scheduleAudio(time) {
  stopAllAudio();
  if (!audioCtx) return;
  for (var i = 0; i < data.audioClips.length; i++) {
    scheduleClipFromTime(data.audioClips[i], time);
  }
}

// Called from each decode-completion callback. If playback is active and the
// clip overlaps the playhead, start it without disturbing other clips.
function onClipDecoded(clipId) {
  if (!isPlaying || !audioCtx) return;
  for (var i = 0; i < activeSources.length; i++) {
    if (activeSources[i].clipId === clipId) return;
  }
  for (var i = 0; i < data.audioClips.length; i++) {
    if (data.audioClips[i].id === clipId) {
      scheduleClipFromTime(data.audioClips[i], currentTime);
      return;
    }
  }
}

function stopAllAudio() {
  for (var i = 0; i < activeSources.length; i++) {
    try { activeSources[i].source.stop(); } catch(e) {}
    try { activeSources[i].source.disconnect(); } catch(e) {}
    try { activeSources[i].gain.disconnect(); } catch(e) {}
  }
  activeSources = [];
}
`;

const PLAYER_TRANSITIONS = `
// ── Transition rendering ──────────────────────────────────────────────

function renderTransition(viewerArea, transInfo) {
  var fromShot = transInfo.fromShot;
  var toShot = transInfo.toShot;
  var progress = transInfo.progress;
  var type = transInfo.transition.type;
  var fromScene = getSceneForShot(fromShot.id);
  var toScene = getSceneForShot(toShot.id);

  if (type === 'dissolve') {
    var stack = document.createElement('div');
    stack.className = 'transition-stack';

    var fromLayer = document.createElement('div');
    fromLayer.className = 'transition-layer';
    fromLayer.style.opacity = (1 - progress).toString();
    fromLayer.appendChild(createShotMedia(fromShot, fromScene));
    stack.appendChild(fromLayer);

    var toLayer = document.createElement('div');
    toLayer.className = 'transition-layer';
    toLayer.style.opacity = progress.toString();
    toLayer.appendChild(createShotMedia(toShot, toScene));
    stack.appendChild(toLayer);

    // Clear viewer content but keep label/overlay
    clearViewerContent(viewerArea);
    viewerArea.insertBefore(stack, viewerArea.firstChild);
    return;
  }

  if (type === 'fade_to_black' || type === 'fade_from_black') {
    var isFirst = progress < 0.5;
    var active = isFirst ? fromShot : toShot;
    var activeScene = isFirst ? fromScene : toScene;
    var fadeOpacity = isFirst ? 1 - progress * 2 : (progress - 0.5) * 2;

    clearViewerContent(viewerArea);
    viewerArea.style.background = '#000';
    var layer = document.createElement('div');
    layer.className = 'transition-layer';
    layer.style.opacity = fadeOpacity.toString();
    layer.appendChild(createShotMedia(active, activeScene));
    viewerArea.insertBefore(layer, viewerArea.firstChild);
    return;
  }
}

function clearViewerContent(viewerArea) {
  // Remove everything except #shot-label, #review-overlay, #fullscreen-btn, #content-overlay
  var children = viewerArea.childNodes;
  for (var i = children.length - 1; i >= 0; i--) {
    var child = children[i];
    if (child.id === 'shot-label' || child.id === 'review-overlay' || child.id === 'fullscreen-btn' ||
        child.id === 'content-overlay') continue;
    viewerArea.removeChild(child);
  }
}

// Composes "EXT. DINER \\u2014 DAY", omitting empty parts so a scene with no location set
// (empty name) doesn't leave a stray double space; mirrors composeSceneCaptionRest
// (sceneCaption.ts) in the main app. Shared by the three heading sites below.
function composeSceneHeading(scene) {
  var parts = [];
  if (scene.locationType) parts.push(scene.locationType + '.');
  if (scene.name) parts.push(scene.name.toUpperCase());
  var head = parts.join(' ');
  var tailParts = [];
  if (head) tailParts.push(head);
  if (scene.timeOfDay) tailParts.push(scene.timeOfDay);
  return tailParts.join(' \\u2014 ');
}

function createShotMedia(shot, scene) {
  if (shot.videoDataUrl) {
    var vid = document.createElement('video');
    vid.src = shot.videoDataUrl;
    vid.muted = true;
    vid.playsInline = true;
    vid.preload = 'auto';
    return vid;
  }
  if (shot.thumbnailDataUrl) {
    var img = document.createElement('img');
    img.src = shot.thumbnailDataUrl;
    img.alt = 'Shot ' + shot.number;
    return img;
  }
  var color = scene ? scene.color : '#333';
  var card = document.createElement('div');
  card.className = 'placeholder-card';
  card.style.background = 'linear-gradient(135deg, ' + color + ' 0%, ' + color + '88 50%, ' + color + '44 100%)';
  var numEl = document.createElement('div');
  numEl.className = 'shot-num';
  numEl.textContent = shot.number || '?';
  card.appendChild(numEl);
  var hdEl = document.createElement('div');
  hdEl.className = 'heading';
  hdEl.textContent = scene ? composeSceneHeading(scene) : '';
  card.appendChild(hdEl);
  var lensEl = document.createElement('div');
  lensEl.className = 'lens';
  lensEl.textContent = shot.focalLength + 'mm f/' + shot.fStop;
  card.appendChild(lensEl);
  return card;
}
`;

const PLAYER_VIDEO = `
// ── Video sync ────────────────────────────────────────────────────────

function syncVideoPlay() {
  var vids = document.querySelectorAll('#viewer-area video');
  for (var i = 0; i < vids.length; i++) {
    try { vids[i].play(); } catch(e) {}
  }
}

function syncVideoPause() {
  var vids = document.querySelectorAll('#viewer-area video');
  for (var i = 0; i < vids.length; i++) {
    try { vids[i].pause(); } catch(e) {}
  }
}

function syncVideoSeek() {
  // Sync currently visible video to the right time offset
  var shot = getShotAtTime(currentTime);
  if (!shot || !shot.videoDataUrl) return;
  var shotStart = getShotStartTime(shot.id);
  var offset = currentTime - shotStart;
  var vids = document.querySelectorAll('#viewer-area video');
  for (var i = 0; i < vids.length; i++) {
    if (vids[i].src === shot.videoDataUrl || vids[i].getAttribute('data-shot-id') === String(shot.id)) {
      vids[i].currentTime = offset;
    }
  }
}
`;

const PLAYER_COMMENTS = `
// ── Comments ──────────────────────────────────────────────────────────

function toggleComments() {
  commentPanelOpen = !commentPanelOpen;
  var panel = document.getElementById('comments-panel');
  if (panel) {
    if (commentPanelOpen) panel.classList.add('open');
    else panel.classList.remove('open');
  }
  renderComments();
  // The panel is a flex sibling animating width (0.2s), which squeezes #viewer-area without any
  // window resize event — the media re-letterboxes via CSS but the overlay holds a stale pixel
  // rect, drifting doodles/arrows off the image. Re-layout now and again just past the transition.
  layoutContentOverlay();
  setTimeout(layoutContentOverlay, 250);
}

function renderComments() {
  var list = document.querySelector('#comments-panel .comments-list');
  if (!list) return;
  list.innerHTML = '';

  var shot = getShotAtTime(currentTime);
  if (!shot) return;

  var comments = getCommentsForShot(shot.id);
  // Sort: top-level first by date, then replies nested
  var topLevel = [];
  var replies = {};
  for (var i = 0; i < comments.length; i++) {
    var c = comments[i];
    if (!c.parentCommentId) {
      topLevel.push(c);
    } else {
      if (!replies[c.parentCommentId]) replies[c.parentCommentId] = [];
      replies[c.parentCommentId].push(c);
    }
  }

  topLevel.sort(function(a, b) { return a.createdAt.localeCompare(b.createdAt); });

  for (var j = 0; j < topLevel.length; j++) {
    list.appendChild(buildCommentEl(topLevel[j]));
    var childReplies = replies[topLevel[j].id] || [];
    childReplies.sort(function(a, b) { return a.createdAt.localeCompare(b.createdAt); });
    for (var k = 0; k < childReplies.length; k++) {
      list.appendChild(buildCommentEl(childReplies[k], true));
    }
  }

  if (topLevel.length === 0) {
    var empty = document.createElement('div');
    empty.style.cssText = 'color: var(--text-muted); font-size: 12px; padding: 12px 0;';
    empty.textContent = 'No comments for this shot.';
    list.appendChild(empty);
  }
}

function buildCommentEl(comment, isReply) {
  var el = document.createElement('div');
  el.className = 'comment-item' + (isReply ? ' reply' : '');

  var author = document.createElement('div');
  author.className = 'comment-author';
  author.textContent = comment.author;
  if (comment.annotationSvg) {
    var badge = document.createElement('span');
    badge.className = 'comment-doodle-badge';
    badge.title = 'Has a drawing';
    badge.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>';
    author.appendChild(badge);
  }
  el.appendChild(author);

  var time = document.createElement('div');
  time.className = 'comment-time';
  var d = new Date(comment.createdAt);
  time.textContent = d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (comment.timecode !== null && comment.timecode !== undefined) {
    time.textContent += ' @ ' + formatTimecode(comment.timecode);
  }
  el.appendChild(time);

  var text = document.createElement('div');
  text.className = 'comment-text';
  text.textContent = comment.text;
  el.appendChild(text);

  return el;
}
`;

const PLAYER_OVERLAYS = `
// ── Content overlay: movement arrows + doodles ────────────────────────
// Both are display-time composites in the app too — the render never contains them — so the player
// has to draw them like every other surface. They share one node because they share one frame: the
// letterboxed shot rect, project-aspect authoritative (matching Viewer.tsx), NOT the img element box.

var SVG_NS = 'http://www.w3.org/2000/svg';

function layoutContentOverlay() {
  var viewerArea = document.getElementById('viewer-area');
  var overlay = document.getElementById('content-overlay');
  if (!viewerArea || !overlay) return;
  var ar = parseAspectRatio(data.aspectRatio) || 16 / 9;
  var rect = computeContentRect(viewerArea.clientWidth, viewerArea.clientHeight, 1000, 1000 / ar);
  overlay.style.left = rect.x + 'px';
  overlay.style.top = rect.y + 'px';
  overlay.style.width = rect.width + 'px';
  overlay.style.height = rect.height + 'px';
}

function clearContentOverlay() {
  var overlay = document.getElementById('content-overlay');
  if (overlay) overlay.innerHTML = '';
}

function renderContentOverlay(shot) {
  var overlay = document.getElementById('content-overlay');
  if (!overlay) return;
  overlay.innerHTML = '';
  layoutContentOverlay();
  // Arrows first: they are set decoration, a reviewer's strokes go on top of them.
  renderMovementArrows(overlay, shot);
  renderDoodles(overlay, shot);
}

function renderMovementArrows(overlay, shot) {
  var arrows = shot.movementArrows;
  if (!arrows || arrows.length === 0) return;
  var sprites = data.arrowSprites || [];
  for (var i = 0; i < arrows.length; i++) {
    var a = arrows[i];
    var sprite = sprites[a.sprite];
    if (!sprite) continue;
    var span = document.createElement('span');
    span.className = 'movement-arrow';
    span.style.left = a.leftPct + '%';
    span.style.top = a.topPct + '%';
    span.style.width = a.widthPct + '%';
    span.style.height = a.heightPct + '%';
    span.style.transform = a.transform;
    // Consumed by the mask rule in the stylesheet — the sprite is a mask, never an image source.
    span.style.setProperty('--arw', 'url(' + sprite + ')');
    overlay.appendChild(span);
  }
}

function renderDoodles(overlay, shot) {
  var paths = [];
  for (var i = 0; i < data.comments.length; i++) {
    var c = data.comments[i];
    // screenplayPage strokes are page geometry, never shot geometry — the same exclusion
    // mergedAnnotationForShot applies when handing doodles to Unity.
    if (c.shotId !== shot.id || !c.annotationSvg || c.screenplayPage) continue;
    try {
      var parsed = JSON.parse(c.annotationSvg);
      if (parsed && parsed.paths) paths = paths.concat(parsed.paths);
    } catch (e) { /* skip malformed, as the live overlay does */ }
  }
  if (paths.length === 0) return;

  // One svg for every author's strokes, each keeping its own colour — coordinates are normalized
  // 0-1, so the viewBox does the scaling and preserveAspectRatio must not re-letterbox them.
  var svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'doodle-layer');
  svg.setAttribute('viewBox', '0 0 1 1');
  svg.setAttribute('preserveAspectRatio', 'none');
  for (var j = 0; j < paths.length; j++) {
    var p = paths[j];
    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', p.d);
    path.setAttribute('stroke', p.stroke);
    // Stored width, as-is. The live overlay rescales highlighter strokes against a screenplay-page
    // constant; importing paginator geometry into a standalone player would buy a cosmetic width
    // match on the highlighter-over-a-shot case and nothing else. Known delta.
    path.setAttribute('stroke-width', String(p.strokeWidth));
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    path.setAttribute('opacity', String(p.opacity == null ? 1 : p.opacity));
    svg.appendChild(path);
  }
  overlay.appendChild(svg);
}
`;

const PLAYER_INIT = `
// ── Update functions ──────────────────────────────────────────────────

var lastRenderedShotId = null;

function updateViewer() {
  var viewerArea = document.getElementById('viewer-area');
  if (!viewerArea) return;

  var shot = getShotAtTime(currentTime);
  if (!shot) return;

  var scene = getSceneForShot(shot.id);
  var transInfo = getTransitionAtTime(currentTime);

  // During transitions, always re-render
  if (transInfo) {
    lastRenderedShotId = null;
    viewerArea.style.background = '#000';
    renderTransition(viewerArea, transInfo);
    updateShotLabel(shot, scene);
    updateReviewOverlay(shot);
    // Annotations are deliberately dark across a dissolve/fade: two shots are on screen at once and
    // one shot's strokes and arrows over both reads as a glitch. lastRenderedShotId is null here, so
    // the first frame past the transition re-renders them.
    clearContentOverlay();
    if (commentPanelOpen) renderComments();
    return;
  }

  // Only re-render viewer content if shot changed
  if (shot.id !== lastRenderedShotId) {
    lastRenderedShotId = shot.id;
    clearViewerContent(viewerArea);

    if (shot.videoDataUrl) {
      viewerArea.style.background = '#000';
      var vid = document.createElement('video');
      vid.src = shot.videoDataUrl;
      vid.setAttribute('data-shot-id', String(shot.id));
      vid.muted = true;
      vid.playsInline = true;
      vid.preload = 'auto';
      var shotStart = getShotStartTime(shot.id);
      vid.currentTime = currentTime - shotStart;
      if (isPlaying) vid.play().catch(function(){});
      viewerArea.insertBefore(vid, viewerArea.firstChild);
    } else if (shot.thumbnailDataUrl) {
      viewerArea.style.background = '#000';
      var img = document.createElement('img');
      img.src = shot.thumbnailDataUrl;
      img.alt = 'Shot ' + shot.number;
      viewerArea.insertBefore(img, viewerArea.firstChild);
    } else {
      var color = scene ? scene.color : '#333';
      viewerArea.style.background = 'linear-gradient(135deg, ' + color + ' 0%, ' + color + '88 50%, ' + color + '44 100%)';
      var card = document.createElement('div');
      card.className = 'placeholder-card';
      var heading = scene ? composeSceneHeading(scene) : '';
      var numEl = document.createElement('div');
      numEl.className = 'shot-num';
      numEl.textContent = shot.number || '?';
      card.appendChild(numEl);
      var hdEl = document.createElement('div');
      hdEl.className = 'heading';
      hdEl.textContent = heading;
      card.appendChild(hdEl);
      var lensEl = document.createElement('div');
      lensEl.className = 'lens';
      lensEl.textContent = shot.focalLength + 'mm f/' + shot.fStop;
      card.appendChild(lensEl);
      if (shot.notes) {
        var notesEl = document.createElement('div');
        notesEl.className = 'notes';
        notesEl.textContent = shot.notes;
        card.appendChild(notesEl);
      }
      viewerArea.insertBefore(card, viewerArea.firstChild);
    }

    updateShotLabel(shot, scene);
    updateReviewOverlay(shot);
    renderContentOverlay(shot);
    if (commentPanelOpen) renderComments();
  }
}

function escapeHtmlInline(s) {
  var d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

function updateShotLabel(shot, scene) {
  var label = document.getElementById('shot-label');
  if (!label) return;
  var heading = scene ? composeSceneHeading(scene) : '';
  var parts = [];
  if (shot.shotSize) parts.push(shot.shotSize);
  if (shot.shotAngle) parts.push(shot.shotAngle);
  if (shot.shotMovement) parts.push(shot.shotMovement);
  label.textContent = '';
  var s1 = document.createElement('span'); s1.textContent = heading; label.appendChild(s1);
  var s2 = document.createElement('span'); s2.textContent = 'Shot ' + shot.number; label.appendChild(s2);
  var s3 = document.createElement('span'); s3.textContent = shot.focalLength + 'mm f/' + shot.fStop; label.appendChild(s3);
  if (parts.length > 0) { var s4 = document.createElement('span'); s4.textContent = parts.join(' / '); label.appendChild(s4); }
}

function updateReviewOverlay(shot) {
  var overlay = document.getElementById('review-overlay');
  if (!overlay) return;
  overlay.innerHTML = '';

  var approval = getApprovalForShot(shot.id);
  if (approval) {
    var pill = document.createElement('span');
    pill.className = 'approval-pill';
    pill.style.background = APPROVAL_COLORS[approval.status] || '#888';
    pill.textContent = approval.status.replace('_', ' ');
    pill.onclick = function() { toggleComments(); };
    overlay.appendChild(pill);
  }

  var count = getCommentCount(shot.id);
  var btn = document.createElement('span');
  btn.className = 'comment-count-btn';
  btn.textContent = count > 0 ? String(count) : '\\uD83D\\uDCAC';
  btn.onclick = function() { toggleComments(); };
  overlay.appendChild(btn);
}

// ── Timeline rendering ────────────────────────────────────────────────

function renderTimeline() {
  renderRuler();
  renderShotLane();
  renderAudioLane();
  updatePlayhead();

  // Set inner width
  var inner = document.getElementById('timeline-inner');
  if (inner) {
    var totalWidth = Math.max(data.totalDuration * pixelsPerSecond, 100);
    inner.style.width = totalWidth + 'px';
  }
}

function renderRuler() {
  var ruler = document.getElementById('ruler');
  if (!ruler) return;
  // Clear ticks but keep ruler
  ruler.innerHTML = '';

  var totalWidth = data.totalDuration * pixelsPerSecond;
  // Determine tick interval based on zoom
  var interval = 1;
  if (pixelsPerSecond < 30) interval = 10;
  else if (pixelsPerSecond < 60) interval = 5;
  else if (pixelsPerSecond < 120) interval = 2;

  for (var t = 0; t <= data.totalDuration; t += interval) {
    var x = t * pixelsPerSecond;
    var tick = document.createElement('div');
    tick.className = 'tick';
    tick.style.left = x + 'px';
    ruler.appendChild(tick);

    var lbl = document.createElement('span');
    lbl.className = 'tick-label';
    lbl.style.left = (x + 3) + 'px';
    lbl.textContent = formatTimecode(t);
    ruler.appendChild(lbl);
  }
}

function renderShotLane() {
  var lane = document.getElementById('shot-lane');
  if (!lane) return;
  lane.innerHTML = '';

  var shots = allShots();
  var cumX = 0;
  var GAP_INSET = ${LAYOUT.SHOT_BAR_GAP_INSET};

  for (var i = 0; i < shots.length; i++) {
    var shot = shots[i];
    var totalW = shot.duration * pixelsPerSecond;
    var barW = Math.max(totalW - GAP_INSET * 2, 4);
    var barLeft = cumX + GAP_INSET;
    var scene = getSceneForShot(shot.id);
    var color = scene ? scene.color : '#444';

    var bar = document.createElement('div');
    bar.className = 'shot-bar' + (shot.id === (getShotAtTime(currentTime) || {}).id ? ' active' : '');
    bar.style.left = barLeft + 'px';
    bar.style.width = barW + 'px';
    bar.setAttribute('data-shot-id', String(shot.id));

    // Fill area: thumbnail or colored placeholder
    var fill = document.createElement('div');
    fill.className = 'shot-bar-fill';
    if (shot.thumbnailDataUrl) {
      var thumb = document.createElement('img');
      thumb.src = shot.thumbnailDataUrl;
      thumb.alt = '';
      fill.appendChild(thumb);
    } else {
      fill.style.background = color;
      var plNum = document.createElement('span');
      plNum.className = 'placeholder-num';
      plNum.textContent = shot.number || '?';
      fill.appendChild(plNum);
    }

    // Video badge (top-left)
    if (shot.videoDataUrl) {
      var vBadge = document.createElement('span');
      vBadge.className = 'shot-video-badge';
      vBadge.textContent = '\\u25B6';
      fill.appendChild(vBadge);
    }

    // Comment dot (top-right) — only when comments exist
    var shotComments = getCommentCount(shot.id);
    if (shotComments > 0) {
      var dot = document.createElement('span');
      dot.className = 'shot-comment-dot';
      var dotApproval = getApprovalForShot(shot.id);
      dot.style.background = APPROVAL_COLORS[(dotApproval && dotApproval.status) || 'pending'] || '#f5c542';
      dot.title = shotComments + ' comment' + (shotComments > 1 ? 's' : '');
      fill.appendChild(dot);
    }

    bar.appendChild(fill);

    // Info pill at bottom-right
    var info = document.createElement('div');
    info.className = 'shot-bar-info';

    var numSpan = document.createElement('span');
    numSpan.className = 'shot-num';
    numSpan.textContent = shot.number || '?';
    info.appendChild(numSpan);

    var durSpan = document.createElement('span');
    durSpan.textContent = shot.duration.toFixed(1) + 's';
    info.appendChild(durSpan);

    fill.appendChild(info);

    bar.onclick = (function(shotStartTime) {
      return function() { seekTo(shotStartTime); };
    })(cumX / pixelsPerSecond);

    lane.appendChild(bar);

    // Transition diamond
    if (i < shots.length - 1) {
      for (var k = 0; k < data.transitions.length; k++) {
        var tr = data.transitions[k];
        if (tr.fromShotId === shot.id && tr.toShotId === shots[i + 1].id && tr.type !== 'cut') {
          var diamond = document.createElement('div');
          diamond.className = 'transition-diamond';
          diamond.style.left = (cumX + totalW) + 'px';
          lane.appendChild(diamond);
          break;
        }
      }
    }

    cumX += totalW;
  }
}

function renderAudioLane() {
  var lane = document.getElementById('audio-lane');
  if (!lane) return;
  lane.innerHTML = '';

  var LANE_H = ${AUDIO_SUBLANE_HEIGHT};
  var LANE_NAMES = ${JSON.stringify(AUDIO_LANE_NAMES)};

  // Lane dividers and labels
  for (var l = 0; l < 4; l++) {
    if (l > 0) {
      var divider = document.createElement('div');
      divider.className = 'audio-lane-divider';
      divider.style.top = (l * LANE_H) + 'px';
      lane.appendChild(divider);
    }
    var laneLbl = document.createElement('div');
    laneLbl.className = 'audio-lane-label';
    laneLbl.style.top = (l * LANE_H + 2) + 'px';
    laneLbl.textContent = LANE_NAMES[l];
    lane.appendChild(laneLbl);
  }

  // Clips positioned by lane
  for (var i = 0; i < data.audioClips.length; i++) {
    var clip = data.audioClips[i];
    var x = clip.startTime * pixelsPerSecond;
    var w = clip.duration * pixelsPerSecond;
    var clipLane = clip.lane || 0;

    var bar = document.createElement('div');
    bar.className = 'audio-clip-bar';
    bar.style.left = x + 'px';
    bar.style.width = w + 'px';
    bar.style.top = (clipLane * LANE_H) + 'px';
    bar.style.background = clip.color || '#4a90d9';

    // Render waveform if peaks available
    if (clip.peaks && clip.peaks.length > 0) {
      var canvas = document.createElement('canvas');
      canvas.width = Math.max(Math.round(w), 1);
      canvas.height = LANE_H;
      renderWaveform(canvas, clip.peaks, clip.color || '#4a90d9');
      bar.appendChild(canvas);
    }

    var lbl = document.createElement('div');
    lbl.className = 'audio-clip-label';
    lbl.textContent = clip.label;
    bar.appendChild(lbl);

    lane.appendChild(bar);
  }
}

function renderWaveform(canvas, peaks, color) {
  var ctx = canvas.getContext('2d');
  if (!ctx) return;
  var w = canvas.width;
  var h = canvas.height;
  var mid = h / 2;

  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = 'rgba(255,255,255,0.2)';

  var step = peaks.length / w;
  for (var i = 0; i < w; i++) {
    var idx = Math.floor(i * step);
    var val = Math.abs(peaks[idx] || 0);
    var barH = val * mid * 0.9;
    ctx.fillRect(i, mid - barH, 1, barH * 2);
  }
}

function updatePlayhead() {
  var playhead = document.getElementById('playhead');
  if (!playhead) return;

  var rulerH = ${LAYOUT.RULER_HEIGHT};
  var shotH = ${LAYOUT.SHOT_LANE_HEIGHT};
  var audioH = data.audioClips.length > 0 ? ${LAYOUT.AUDIO_LANE_HEIGHT} : 0;

  playhead.style.left = (currentTime * pixelsPerSecond) + 'px';
  playhead.style.height = (rulerH + shotH + audioH) + 'px';
}

function updateTimeline() {
  // Lightweight update: just move playhead and update active shot highlight
  updatePlayhead();

  // Update active shot bar highlight
  var activeShotEl = document.querySelector('#shot-lane .shot-bar.active');
  var curShot = getShotAtTime(currentTime);
  if (activeShotEl) {
    var barId = activeShotEl.getAttribute('data-shot-id');
    if (!curShot || String(curShot.id) !== barId) {
      activeShotEl.classList.remove('active');
    }
  }
  if (curShot) {
    var newBar = document.querySelector('#shot-lane .shot-bar[data-shot-id="' + curShot.id + '"]');
    if (newBar && !newBar.classList.contains('active')) {
      newBar.classList.add('active');
    }
  }
}

// ── Initialization ────────────────────────────────────────────────────

function init() {
  buildUI();

  // Initial render
  updateViewer();
  renderTimeline();
  updateTimecodeDisplay();

  // Pre-decode audio (creates AudioContext lazily)
  if (data.audioClips.length > 0 && data.audioClips.some(function(c) { return !!c.rawBase64 || !!c.mediaUrl; })) {
    // Defer until first interaction to satisfy autoplay policy
  }

  // Transport buttons
  document.getElementById('play-btn').onclick = function() {
    // Init audio context on first play (autoplay policy)
    if (!audioCtx && data.audioClips.some(function(c) { return !!c.rawBase64 || !!c.mediaUrl; })) {
      initAudioContext();
      preDecodeAudio();
    }
    togglePlay();
  };
  document.getElementById('skip-back').onclick = function() { seekTo(currentTime - 1); };
  document.getElementById('skip-fwd').onclick = function() { seekTo(currentTime + 1); };

  // Click on ruler/shot-lane to seek
  document.getElementById('ruler').onclick = function(e) {
    var rect = this.getBoundingClientRect();
    var scrollEl = document.getElementById('timeline-scroll');
    var x = e.clientX - rect.left + (scrollEl ? scrollEl.scrollLeft : 0);
    seekTo(x / pixelsPerSecond);
  };

  document.getElementById('shot-lane').onclick = function(e) {
    if (e.target.closest && e.target.closest('.shot-bar')) return; // handled by shot bar
    var rect = this.getBoundingClientRect();
    var scrollEl = document.getElementById('timeline-scroll');
    var x = e.clientX - rect.left + (scrollEl ? scrollEl.scrollLeft : 0);
    seekTo(x / pixelsPerSecond);
  };

  // Keyboard shortcuts
  document.addEventListener('keydown', function(e) {
    if (e.key === ' ' || e.code === 'Space') {
      e.preventDefault();
      if (!audioCtx && data.audioClips.some(function(c) { return !!c.rawBase64 || !!c.mediaUrl; })) {
        initAudioContext();
        preDecodeAudio();
      }
      togglePlay();
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      seekTo(currentTime - 1);
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      seekTo(currentTime + 1);
    }
    if (e.key === 'Home') {
      e.preventDefault();
      seekTo(0);
    }
    if (e.key === 'End') {
      e.preventDefault();
      seekTo(data.totalDuration);
    }
    if (e.key === 'c' || e.key === 'C') {
      if (!e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        toggleComments();
      }
    }
    // Zoom in/out
    if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      pixelsPerSecond = Math.min(300, pixelsPerSecond + 20);
      syncZoomSlider();
      renderTimeline();
    }
    if (e.key === '-') {
      e.preventDefault();
      pixelsPerSecond = Math.max(20, pixelsPerSecond - 20);
      syncZoomSlider();
      renderTimeline();
    }
    // Fullscreen toggle
    if ((e.key === 'f' || e.key === 'F') && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      toggleFullscreen();
    }
  });

  // Zoom slider input
  var zoomSlider = document.getElementById('zoom-slider');
  if (zoomSlider) {
    zoomSlider.addEventListener('input', function(e) {
      pixelsPerSecond = Number(e.target.value);
      renderTimeline();
    });
  }

  // Window resize → recompute zoom
  window.addEventListener('resize', computeZoom);

  // The content overlay is sized in pixels off the viewer's box, so it has to be re-measured
  // whenever that box changes. Fullscreen moves #viewer-area without always firing a window resize,
  // hence both listeners (webkit- prefix for older Safari, which is also the WebView here).
  window.addEventListener('resize', layoutContentOverlay);
  document.addEventListener('fullscreenchange', layoutContentOverlay);
  document.addEventListener('webkitfullscreenchange', layoutContentOverlay);
}

init();
`;
